<template>
  <main class="auth-form-page">
    <AppNavBar :border="false" />

    <section class="auth-form-head">
      <h1>{{ t('auth.importTitle') }}</h1>
      <p>{{ t('auth.importWalletTip') }}</p>
    </section>

    <section class="auth-form-section">
      <van-field
        v-model="secret"
        class="auth-form-field auth-form-field--textarea"
        type="textarea"
        rows="6"
        autosize
        clearable
        :placeholder="t('auth.importPlaceholder')"
      >
        <template #button>
          <button type="button" class="auth-scan-btn" @click="openScanner">
            <app-image src="@/assets/images/common_scancode_icon3.png" alt="" />
          </button>
        </template>
      </van-field>
    </section>

    <section class="safe-tips">
      <div v-for="item in tips" :key="item.text">
        <app-image :src="item.icon" alt="" />
        <span>{{ item.text }}</span>
      </div>
    </section>

    <AuthThemeButton block class="auth-submit-btn" :loading="loading" @click="continueImport">
      {{ t('auth.continue') }}
    </AuthThemeButton>
  </main>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { showFailToast } from 'vant';
import AuthThemeButton from '@/view/auth/components/AuthThemeButton.vue';
import { useWalletScanner } from '@/composables/useWalletScanner';
import { getRouteState, navigateWithState } from '@/utils/route-state';
import { saveTempImportSecret } from '@/utils/walletSession';
import lockIcon from '@/assets/images/loginImage/lock_icon.png';
import eyeIcon from '@/assets/images/loginImage/eye_icon.png';
import writeIcon from '@/assets/images/loginImage/write_icon.png';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { openWalletScanner } = useWalletScanner();
const pageState = computed(() => getRouteState(route, 'auth-import-wallet', {}));

const secret = ref('');
const errorText = ref('');
const loading = ref(false);

const tips = computed(() => [
  { icon: lockIcon, text: t('auth.importTip1') },
  { icon: eyeIcon, text: t('auth.importTip2') },
  { icon: writeIcon, text: t('auth.importTip3') }
]);

function applyScanResult() {
  if (pageState.value.scanResult) {
    secret.value = String(pageState.value.scanResult);
    errorText.value = '';
    navigateWithState(router, route.path, {
      namespace: 'auth-import-wallet',
      replace: true,
      payload: {
        ...pageState.value,
        scanResult: ''
      }
    });
  }
}

function openScanner() {
  openWalletScanner({
    back: '/auth/import-wallet',
    stateNamespace: 'auth-import-wallet',
    statePayload: { ...pageState.value }
  });
}

async function deriveAddress() {
  const { Wallet } = await import('ethers');
  const value = secret.value.trim();
  if (!value) throw new Error(t('auth.importPlaceholder'));

  if (/^0x[0-9a-fA-F]{64}$/.test(value)) {
    return new Wallet(value).address;
  }

  const words = value.split(/\s+/).filter(Boolean);
  if (words.length >= 12) {
    return Wallet.fromPhrase(words.join(' ')).address;
  }

  throw new Error(t('auth.importInvalid'));
}

async function continueImport() {
  loading.value = true;
  errorText.value = '';
  try {
    const address = await deriveAddress();
    const value = secret.value.trim();
    const importType = /^0x[0-9a-fA-F]{64}$/.test(value) ? 'privateKey' : 'mnemonic';
    saveTempImportSecret({
      address,
      secret: value,
      type: importType
    });
    navigateWithState(router, '/auth/create-wallet', {
      namespace: 'auth-create-wallet',
      payload: {
        type: 'import',
        importAddress: address
      }
    });
  } catch (error) {
    showFailToast(error?.message || t('auth.importInvalid'));
  } finally {
    loading.value = false;
  }
}

watch(() => pageState.value.scanResult, applyScanResult);

onMounted(applyScanResult);
</script>

<style scoped lang="scss">
@use '@/styles/auth-form.scss';

.import-error {
  margin: 6px 0 0 16px;
  color: #f53f3f;
  font-size: 12px;
}

.safe-tips {
  margin-top: 20px;

  div {
    display: flex;
    align-items: flex-start;
    margin-top: 20px;

    &:first-child {
      margin-top: 0;
    }
  }

  .app-image {
    width: 22px;
    height: 22px;
    margin-right: 16px;
    object-fit: contain;
  }

  span {
    color: #222222;
    font-size: 14px;
    line-height: 20px;
  }
}
</style>
